import React, { useEffect, useState } from 'react';
import { useQueryPromise, fetchPostQuery } from '/src/api';
import { useModalsContext } from '/src/providers';
import { Form, Loader, Section } from '/src/components';
import graphQLQueries from './graph-ql-queries';
import * as styles from './styles.scss';

const EMAIL_PREFERENCES_PAGES = {
    'GUEST': 'GUEST',
    'CUSTOMER_PAGE': 'CUSTOMER_PAGE',
    'CHECKOUT': 'CHECKOUT',
};

const NotificationPreferences = ({
    pageType,
    email,
    emailToken,
    translate,
    nextStep,
    H2,
}) => {
    const { showModal, MODALS_TYPES } = useModalsContext();
    const {
        data,
        isLoading,
        runQueryPromise,
        setValues,
    } = useQueryPromise({
        data,
        query: graphQLQueries.getPreferencesOpt,
        queryKey: ['getPreferencesOpt'],
        isMethodPost: true,
    });
    const [checked, setChecked] = useState({});
    const { items, page_config, phone } = data || {};
    const {
        title,
        description,
        bottom_description,
        buttons = [],
    } = page_config || {};
    const preferences = items || [];

    const updatePreferences = variables => {
        setValues({ isLoading: true });

        fetchPostQuery({
            query: graphQLQueries.updateNotificationPreferences,
            variables: {
                input: {
                    ...variables,
                    page_type: pageType,
                    email,
                    emailToken,
                }
            },
        }).then(({ updateNotificationPreferences }) => {
            setChecked({});
            setValues({ data: updateNotificationPreferences, isLoading: false });
        }).catch(errors => {
            setValues({ isLoading: false });
            showModal(MODALS_TYPES.ERROR, { errors });
        })
    };

    const findSmsPreference = (preferences, isChecked) => preferences.find(({
        customer_preference
    }) => customer_preference.find(({
        notification_type, is_checked
    }) => notification_type === 'sms' && is_checked === isChecked ));

    const handleCheckButtons = variables => {
        const isSmsPreference = findSmsPreference(preferences, false);
        const { addition_preference } = variables;

        if (isSmsPreference && addition_preference === 'SUBSCRIBE_TO_ALL') {
            showModal(MODALS_TYPES.SMS_PHONE, {
                phone: phone,
                setPhone: phone => updatePreferences({
                    ...variables,
                    phone,
                    preferences: preferences.map(({ id, customer_preference }) => ({ id, customer_preference })),
                })
            });
        } else {
            updatePreferences({
                ...variables,
                preferences: preferences.map(({ id, customer_preference }) => ({ id, customer_preference })),
            });
        }
    };

    const handleSave = () => {
        const preferences = Object.keys(checked).map(key => ({ id: key, customer_preference: checked[key] }));
        const isSmsPreference = findSmsPreference(preferences, true);

        if (isSmsPreference) {
            showModal(MODALS_TYPES.SMS_PHONE, {
                phone: phone,
                setPhone: phone => updatePreferences({
                    phone,
                    preferences,
                })
            });
        } else {
            updatePreferences({ preferences });
        }
    };

    const handleSavePhone = () => {
        const preferences = Object.keys(checked).map(key => ({ id: key, customer_preference: checked[key] }));

        showModal(MODALS_TYPES.SMS_PHONE, {
            phone: phone,
            setPhone: phone => updatePreferences({
                phone,
                preferences,
            })
        })
    };

    const checkIsFunction = () => typeof nextStep === 'function';

    useEffect(() => {
        runQueryPromise({
            variables: {
                input: {
                    page_type: pageType,
                    email,
                    email_token: emailToken,
                }
            }
        }).then(({ getPreferencesOpt: { items } }) => {
            if (!items.length && checkIsFunction()) {
                nextStep();
            }
        }).catch(errors => {
            if (checkIsFunction()) {
                nextStep();
            } else {
                showModal(MODALS_TYPES.ERROR, { errors })
            }
        })
    }, []);

    return (
        <Loader isLoading={isLoading}>
            {
                !!preferences.length &&
                <>
                    <H2>{title}</H2>
                    <div className={styles.action}>
                        {
                            !!phone &&
                            <div>
                                {`${translate('The phone number to receive SMS notifications. Change.')} `}
                                <button
                                    className={styles.btn}
                                    onClick={handleSavePhone}
                                >
                                    {phone}
                                </button>
                            </div>
                        }
                        <div className={styles.grp}>
                            {
                                buttons.map(({ type, label, disabled }) => (
                                    <button
                                        key={type}
                                        className={styles.btn}
                                        disabled={disabled}
                                        onClick={() => handleCheckButtons({ addition_preference: type })}
                                    >
                                        {label}
                                    </button>
                                ))
                            }
                        </div>
                    </div>
                    {
                        !!description &&
                        <div className={styles.top}>
                            {description}
                        </div>
                    }
                    <Section className={styles.section}>
                        {
                            preferences.map(({
                                id,
                                title,
                                short_description,
                                description,
                                customer_preference,
                            }) => {
                                const isCheckedSwitch = () => {
                                    if (checked[id]?.length === customer_preference.length) {
                                        return !!checked[id].find(({ is_checked }) => is_checked === true);
                                    }

                                    let isChecked = false;
                                    customer_preference.forEach(({ notification_type, is_checked }) => {
                                        const selected = checked[id]?.find(({ notification_type: nf }) => nf === notification_type);

                                        if (selected && selected.is_checked) {
                                            isChecked = true;
                                        } else if (!selected && is_checked) {
                                            isChecked= true;
                                        }
                                    });

                                    return isChecked;
                                };

                                const checkCheckbox = (notificationType, isChecked) => {
                                    if (checked[id]) {
                                        const { notification_type, is_checked } = checked[id].find(({ notification_type }) => notificationType === notification_type) || {};

                                        if (notification_type) {
                                            return is_checked;
                                        }
                                    }

                                    return isChecked;
                                };

                                return (
                                    <div
                                        key={id}
                                        className={styles.preference}
                                    >
                                        <div>
                                            <Form.Switch
                                                name={id}
                                                onChange={
                                                    () => setChecked({
                                                        ...checked,
                                                        [id]: customer_preference.map(({ notification_type }) => ({
                                                            notification_type,
                                                            is_checked: !isCheckedSwitch()
                                                        }))
                                                    })
                                                }
                                                checked={isCheckedSwitch()}
                                            />
                                        </div>
                                        <div className={styles.types}>
                                            {
                                                customer_preference.map(({
                                                    notification_type,
                                                    is_checked,
                                                }) => (
                                                    <Form.Checkbox
                                                        key={`${id}_${notification_type}`}
                                                        name={`${id}_${notification_type}`}
                                                        label={notification_type}
                                                        type="checkbox"
                                                        onChange={
                                                            () => setChecked({
                                                                ...checked,
                                                                [id]: [
                                                                    ...(checked[id]?.filter(({ notification_type: nf }) => nf !== notification_type) || []),
                                                                    {
                                                                        notification_type,
                                                                        is_checked: !checkCheckbox(notification_type, is_checked)
                                                                    }
                                                                ]
                                                            })
                                                        }
                                                        checked={checkCheckbox(notification_type, is_checked)}
                                                    />
                                                ))
                                            }
                                        </div>
                                        <div className={styles.details}>
                                            <div className={styles.title}>
                                                <div className={styles.name}>
                                                    {title}
                                                </div>
                                                {
                                                    !!description &&
                                                    <button
                                                        className={styles.read}
                                                        onClick={() => showModal(MODALS_TYPES.NOTIFICATION_PREFERENCES_READ_MORE, { html: description })}
                                                    >
                                                        {translate('Read more')}
                                                    </button>
                                                }
                                            </div>
                                            <div className={styles.description}>
                                                {short_description}
                                            </div>
                                        </div>
                                    </div>
                                )
                            })
                        }
                    </Section>
                    {
                        !!bottom_description &&
                        <div className={styles.bottom}>
                            {bottom_description}
                        </div>
                    }
                    <div className={styles.save}>
                        <Form.Button
                            onClick={handleSave}
                            disabled={!Object.keys(checked).length || isLoading}
                        >
                            {translate('Save')}
                        </Form.Button>
                        {
                            !!nextStep &&
                            <Form.Button
                                onClick={nextStep}
                                disabled={isLoading}
                            >
                                {translate('Continue')}
                            </Form.Button>
                        }
                    </div>
                </>
            }
        </Loader>
    );
};

export { EMAIL_PREFERENCES_PAGES };

export default NotificationPreferences;
